import Store from "../store";
import server from "./server";
import luckysheetsizeauto from "./resize";
import { openSelection } from "../utils/print";
import { getSheetIndex } from "../methods/get";
import { luckysheetrefreshgrid } from "../global/refresh";
import { getRangeAxis, getRangeByTxt, setRangeShow } from "../global/api";

// CellProtectionCtrl 模块 数据配置在 sheet file 中
class CellProtectionCtrl {
    /**
     * @description 绘制斜线填充的矩形区域
     * @param {CanvasRenderingContext2D} ctx - Canvas 上下文
     * @param {number} startX - 起始 X 坐标
     * @param {number} startY - 起始 Y 坐标
     * @param {number} width - 宽度
     * @param {number} height - 高度
     */
    drawDiagonalLines(ctx, startX, startY, width, height) {
        ctx.save();

        // 最后绘制一个矩形范围即可
        ctx.fillStyle = "rgba(0,0,0,0.015)";
        ctx.fillRect(startX, startY, width, height);

        // 当前缩放比例
        const zoomRatio = Store.zoomRatio;
        const step = 10 * zoomRatio; // 斜线间隔

        ctx.strokeStyle = "rgba(0,0,0,0.08)";
        ctx.lineWidth = 1;
        ctx.lineCap = "round";

        // 创建裁剪区域，限制绘制范围在矩形内
        ctx.beginPath();
        ctx.rect(startX, startY, width, height);
        ctx.clip();

        // 使用旋转绘制斜线，确保所有斜线有相同斜率
        ctx.translate(startX, startY); // 移动坐标系到矩形的左上角
        ctx.rotate(Math.PI / 4); // 旋转角度，45度，可根据需要调整

        // 计算在旋转坐标系中需要绘制的线段范围
        // 确保在旋转后仍能覆盖整个矩形区域
        const diagLen = Math.sqrt(width * width + height * height); // 矩形对角线长度
        const sinVal = Math.abs(Math.sin(Math.PI / 4));
        const cosVal = Math.abs(Math.cos(Math.PI / 4));

        // 计算旋转后矩形的边界
        const maxX = width * cosVal + height * sinVal;
        const maxY = width * sinVal + height * cosVal;

        // 在旋转坐标系中绘制斜线
        ctx.beginPath();
        for (let x = -maxY; x <= maxX; x += step) {
            // 绘制从 (x, -maxY) 到 (x, maxY) 的直线，在原始坐标系中表现为斜线
            ctx.moveTo(x, -maxY);
            ctx.lineTo(x, maxY);
        }

        ctx.stroke();
        ctx.restore();
    }

    /**
     * @description 绘制只读区域! 重要方法，请勿随意修改 [该方法在 drawMain 中调用，用于绘制只读区域]
     * @param {Object} options
     * @param {CanvasRenderingContext2D} options.luckysheetTableContent - Canvas 上下文
     * @param {number} options.bodrder05 - 边框宽度
     * @param {number} options.offsetLeft - 偏移量
     * @param {number} options.offsetTop - 偏移量
     * @param {number} options.scrollWidth - 滚动宽度
     * @param {number} options.scrollHeight - 滚动高度
     *
     */
    render(options = {}) {
        const { luckysheetTableContent, bodrder05, offsetLeft, offsetTop, scrollWidth, scrollHeight } = options;

        // 如果用户没有传递 luckysheetTableContent 则不处理
        if (!luckysheetTableContent) return;

        // 这里一定是从 当前 sheet index 获取的数据，因此，不需要重新过滤
        const cellProtectionConfig = this.getCellProtectionData();
        if (!cellProtectionConfig.length) return;

        cellProtectionConfig.forEach((item) => {
            // 这里保存的一定就是 range 形式： A1:B4 这种字符串，因为数据源是通过 getRangeByTxt 获取的
            let { range, sr = 0, sc = 0, er = 0, ec = 0 } = item;

            if (!range) return;

            // 将 'A1:B2' 转为数组'
            const { column, row } = getRangeByTxt(range);
            sr = row[0];
            sc = column[0];
            er = row[1] + 1;
            ec = column[1] + 1;

            let startX,
                startY,
                endX,
                endY = 0;

            // 绘制区域所受到的偏移影响是一致的
            const offsetX = offsetLeft - scrollWidth - bodrder05;
            const offsetY = offsetTop - scrollHeight - bodrder05;

            if (sc == 0) {
                startX = offsetX;
            } else {
                startX = Store.visibledatacolumn[sc - 1] + offsetX;
            }

            if (sr == 0) {
                startY = offsetY;
            } else {
                startY = Store.visibledatarow[sr - 1] + offsetY;
            }

            if (ec === 0) {
                endX = offsetX;
            } else {
                endX = Store.visibledatacolumn[ec - 1] + offsetX - bodrder05;
            }

            if (er === 0) {
                endY = offsetY;
            } else {
                endY = Store.visibledatarow[er - 1] + offsetY - bodrder05;
            }

            // 使用新的方法绘制斜线
            this.drawDiagonalLines(luckysheetTableContent, startX, startY, endX - startX, endY - startY);
        });
    }

    /**
     * @description 判定其区域是否可编辑 [也就是传入的指定单元格是否已经被保护 ==>  true: 可编辑,未保护； false:不可编辑,被保护 ]
     */
    isEditable(row_index, col_index, sheet_index) {
        // 获取当前 sheet 的 cellProtectionConfig
        const cellProtectionConfig = this.getCellProtectionData(sheet_index);
        if (!cellProtectionConfig.length) return true;

        let editable = true;

        // 不然需要判定传入的单元格是否已经被保护
        cellProtectionConfig.forEach((item) => {
            let { range, sr = 0, sc = 0, er = 0, ec = 0 } = item;

            if (range) {
                // 将 'A1:B2' 转为数组'
                const { column, row } = getRangeByTxt(range);
                sr = row[0];
                sc = column[0];
                er = row[1]; // 这里不能 + 1 哈
                ec = column[1]; // 这里不能 + 1 哈
            }
            if (row_index >= sr && row_index <= er && col_index >= sc && col_index <= ec) {
                editable = false;
            }
        });

        return editable;
    }

    /**
     * @description 更新保护范围 [更新的是 sheet file 的cellProtectionConfig]
     */
    updateSheetFile(cellProtectionConfig, sheet_index) {
        // 更新 sheet config 中
        let file = Store.luckysheetfile[getSheetIndex(sheet_index || Store.currentSheetIndex)];

        // 对传入的 cellProtectionConfig 数组进行去重
        cellProtectionConfig = cellProtectionConfig.reduce((acc, cur) => {
            const exists = acc.some((item) => item.range === cur.range);
            if (!exists) {
                acc.push(cur);
            }
            return acc;
        }, []);

        file.cellProtectionConfig = cellProtectionConfig;

        //刷新表格
        let refreshCanvasTimeOut = setTimeout(function () {
            luckysheetrefreshgrid();
            clearTimeout(refreshCanvasTimeOut);
        }, 1);
    }

    /**
     * @description 更新已保护范围内容区 [用户重新设定保护范围，需要同步更新弹窗内容]
     */
    updateProtectionDrawerContent() {
        // 判定当前是否打开弹窗，如果不是，则不处理
        const $drawer = $("#luckysheet-cell-protection-drawer");
        if (!$drawer.length) return;
        this.setDrawerContent($drawer[0]);
    }

    /**
     * @description 保护当前选区
     */
    current() {
        const cellProtectionConfig = this.getCellProtectionData();

        // 获取当前的选区
        let range = getRangeByTxt();
        // 将其转为字符串形式
        [range] = getRangeAxis(range);

        const newProtection = { range };

        // 存储到 cellProtectionConfig 中
        const newArray = [...cellProtectionConfig, newProtection];

        this.updateSheetFile(newArray);

        this.updateProtectionDrawerContent();

        // 发送协同消息
        this.sync({ range }, "add");
    }

    /**
     * @description 用户选择选区进行保护
     */
    choose() {
        const cellProtectionConfig = this.getCellProtectionData();

        // 获取当前用户选定的选区
        const range_default = Store.luckysheet_select_save[0];

        // 直接唤起单元格选择器
        openSelection(range_default, ({ range }) => {
            // 这里拿到的就是 A1:B2 这种字符串类型，因此需要构建 range 存储
            const newProtection = { range };

            // 存储到 cellProtectionConfig 中
            const newArray = [...cellProtectionConfig, newProtection];

            this.updateSheetFile(newArray);

            this.updateProtectionDrawerContent();

            // 发送协同消息
            this.sync({ range }, "add");
        });
    }

    /**
     * @desscription 查看已保护的单元格
     */
    openDrawer() {
        // 创建抽屉容器
        const drawer = document.createElement("div");
        drawer.id = "luckysheet-cell-protection-drawer";
        drawer.classList.add("luckysheet-cell-protection-drawer");

        this.setDrawerContent(drawer);

        // 将抽屉插入到 body
        document.body.appendChild(drawer);

        luckysheetsizeauto();
    }

    /**
     * @description 设定抽屉的保护选区内容
     */
    setDrawerContent(drawer) {
        const $this = this;

        const cellProtectionConfig = this.getCellProtectionData();
        drawer.innerHTML = `
            <div class="drawer-header">
                <span>全部受保护范围</span>
                <button class="close-btn">&times;</button>
            </div>
            <div class="drawer-body">
                ${cellProtectionConfig
                    .map((item) => {
                        const { range } = item;
                        const sheet_index = Store.currentSheetIndex;
                        const sheetName = Store.luckysheetfile.find((file) => file.index === sheet_index)?.name;
                        return `
                        <div class="protection-item" data-range="${range}">
                            <div class="range">${range} - 工作表 ${sheetName}</div>
                            <div class="desc">禁止编辑</div>
                            <div class="protection-actions">
                                <span class="iconfont luckysheet-icon luckysheet-icon-dingwei"></span>
                                <span class="iconfont luckysheet-icon luckysheet-icon-shanchu"></span>
                            </div>
                        </div>
                        `;
                    })
                    .join("")}
            </div>
        `;

        // 添加关闭按钮事件
        const closeBtn = $(drawer).find(".close-btn");
        closeBtn.off("click").on("click", function () {
            document.body.removeChild(drawer);
            luckysheetsizeauto();
        });

        // 添加定位事件 .luckysheet-icon-dingwei
        const iconBtns = drawer.querySelectorAll(".protection-actions span");
        iconBtns.forEach((btn) => {
            $(btn)
                .off("click")
                .on("click", function () {
                    const range = btn.parentNode.parentNode.dataset.range;
                    // 判定是定位还是删除
                    if (btn.classList.contains("luckysheet-icon-dingwei")) {
                        // 定位
                        setRangeShow(range);
                        // 居中 - 这个需要通过计算得出 left top 的偏移量，实现居中，[待完善]
                        // scroll()
                    } else {
                        // 删除
                        const cellProtectionConfig = $this.getCellProtectionData();
                        const newArray = cellProtectionConfig.filter((item) => item.range !== range);
                        $this.updateSheetFile(newArray); // 更新文件
                        $this.updateProtectionDrawerContent(); // 更新抽屉内容
                        // 发送协同数据
                        $this.sync({ range }, "del");
                    }
                });
        });
    }

    // 获取当前的只读区域
    getCellProtectionData(sheet_index) {
        let file = Store.luckysheetfile[getSheetIndex(sheet_index || Store.currentSheetIndex)];
        return file.cellProtectionConfig || [];
    }

    /**
     * @description 协同相关处理，尽量将操作细分， ‘新增-add’ | ‘删除-delete’
     */
    sync(data = {}, type) {
        if (!server.allowUpdate) return;
        server.saveParam("cp", Store.currentSheetIndex, data, { op: type });
    }

    /**
     * @description 协同相关处理 - 更新单元格保护
     */
    syncUpdate(item) {
        // 这里需要解析对应的参数： op sheet_index range
        const { i, v, op } = item;

        // 当前sheet index 的 cellprotectionConfig
        const cellProtectionConfig = this.getCellProtectionData(i);

        if (op === "add") {
            const newArray = [...cellProtectionConfig, v];
            this.updateSheetFile(newArray, i);
            this.updateProtectionDrawerContent();
        } else if (op === "del") {
            const newArray = cellProtectionConfig.filter((item) => item.range !== v.range);
            this.updateSheetFile(newArray, i);
            this.updateProtectionDrawerContent();
        }
    }
}

export const cellProtectionCtrl = new CellProtectionCtrl();
